// Import firebase services
import { firebaseAuth, propertiesModel } from '@/firebase/firebase-config'

// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'

export const PropertiesRepository = {

	/*
	**  Register the mixins
	*/

	mixins: [

		BugsnagService,
		GenerateUID

	],

	/*
	**  Set up the data
	*/

	data: () => ({
		new_property: {
			id: null,
			type: null,
			meta: {
				created_at: moment().unix(),
				updated_at: moment().unix(),
				deleted_at: null,
				app_id: '1:KA82LQU7lsJy:eKwkfzaQy4aM:HKUl9ACoGeAQ:j1xHCCBLS7UQ:yok9xA1wrkZi',
				version: 1
			},
			attributes: {
				title: null,
				description: null,
				address: {
					street_1: null,
					street_2: null,
					city: null,
					state: null,
					country: 'US',
					zip_code: null
				},
				purchase: {
					price: null,
					price_per_unit: 0,
					date: null
				},
				number_of_stories: 0,
				square_feet: 0,
				lot_size: 0,
				built_in: 0,
				last_renovated: 0,
				opportunity_zone: false,
				taxes: {
					parcel_number: null,
					zoning_code: null
				}
			},
			metrics: {
				units: {
					total: 0,
					breakdown: [],
					total_market_rent: 0,
					total_monthly_rent: 0
				},
				residents: {
					total: 0
				},
				leases: {
					active: {
						total_leases: 0,
						total_balance: 0,
						outstanding_balance: 0
					},
					future: {
						total_leases: 0,
						total_balance: 0
					},
					pending: {
						total_leases: 0,
						total_balance: 0
					}
				}
			},
			links: {
				media: []
			},
			relationships: {
				user: firebaseAuth.currentUser.uid,
				company: null,
				places: null
			}
		}
	}),

	/*
	**  Set the methods
	*/

	methods: {

		createProperty: function() {

			// Generate UID
			this.new_property.id = this.generateUID();

			// Set the company
			if (this.user.relationships.company) this.new_property.relationships.company = this.user.relationships.company;

			// Create doc in database
			propertiesModel.doc(this.new_property.id).set(this.new_property)
			.then(() => {

				// Update company total props metric
				this.updateCompanyTotalProperties();

			})
			.then(() => {

				// Hide the spinner
				this.hideSpinner();

				// Redirect to For You page
				this.$router.push({ name: 'PropertyDetails', params: { id: this.new_property.id } });

				// Throw success message
	    		this.$swal('Success!', 'Your property has been created.', 'success');

			})
			.catch(error => {

				// Hide the spinner
				this.hideSpinner();

				// Throw warning message
    			this.$swal('Uh Oh!', 'Looks like something went wrong when we tried saving your property.', 'error');

    			// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Create Property - Save New Property in Firestore',
					errorType: 'Firestore Database',
					section: 'Create Property',
					component: 'PropertiesRepository',
					error: error
				});

			});

		},

		deletePropertyListing: function(uid) {

			propertiesModel.doc(uid).update({

				'meta.deleted_at': moment().unix(),
				'relationships.user': null

			})
			.then(() => {

				// Update company total props metric
				this.decreaseCompanyTotalProperties();

			})
			.then(() => {

				// Emit the custom event (custom event name, new value)
				this.$emit('deleteProperty', true);

				// Hide the spinner
				this.hideSpinner();

				// Inform the user
				this.$swal('Success!', 'Your property has been deleted.', 'success');

			})
			.catch(error => {

				// Hide the spinner
				this.hideSpinner();

				// Throw warning message
    			this.$swal('Uh Oh!', 'Looks like we ran into some trouble while deleting your property.', 'error');

    			// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Delete Property - Delete Property in Firestore',
					errorType: 'Firestore Database',
					section: 'Properties',
					component: 'PropertiesRepository',
					error: error
				});

			});

		},

		updatePurchasePrice: function() {

			propertiesModel.doc(this.property.id).update({

				'attributes.purchase.price': this.purchase_price,
				'attributes.purchase.date': this.purchase_date,
				'meta.updated_at': moment().unix()

			})
			.then(() => {

				// Hide the spinner
				this.hideSpinner();

				// Close the menu
				this.openMenu = false;

				// Emit the event
				this.$emit('closeAddPurchaseMenu', true);

				// Throw success message
	    		this.$swal('Success!', 'Your purchase details have been updated.', 'success');

			})
			.catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Create Property Purchase - Update Property Purchase Price and Date in Firestore',
					errorType: 'Firestore Database',
					section: 'Create Purchase',
					component: 'PropertiesRepository',
					error: error
				});

			});

		},

		updatePropertyForUnitsUpdates: function(total_units, market_rent) {

			propertiesModel.doc(this.property.id).update({

				'metrics.units.total': total_units,
				'metrics.units.total_market_rent': market_rent,
				'metrics.units.breakdown': this.property_clone.metrics.units.breakdown,
				'meta.updated_at': moment().unix()

			})
			.catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Property Units Total in Firestore',
					errorType: 'Firestore Database',
					section: 'Mixins',
					component: 'PropertiesRepository',
					error: error
				});

			});

		},

		updatePropertyForResidentsUpdates: function(total_residents) {

			propertiesModel.doc(this.property.id).update({

				'metrics.residents.total': total_residents,
				'meta.updated_at': moment().unix()

			})
			.catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Property Residents Total in Firestore',
					errorType: 'Firestore Database',
					section: 'Mixins',
					component: 'PropertiesRepository',
					error: error
				});

			});

		},

		updatePropertyForNewRent: function() {

			propertiesModel.doc(this.property.id).update({

				'metrics.units.total_monthly_rent': this.property_clone.metrics.units.total_monthly_rent + this.new_rent.attributes.amount,
				'meta.updated_at': moment().unix()

			})
			.catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Property Total Monthly Rent in Firestore',
					errorType: 'Firestore Database',
					section: 'Mixins',
					component: 'PropertiesRepository',
					error: error
				});

			});

		}

	}

}